import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MarkersAPI } from '../../api/MarkerEndpoints';
import MarkerTable from './components/MarkerTable';
import MarkerDetailsModal from './components/details/MarkerDetailsModal';
import BulkCsvUploadModal from './components/BulkCsvUploadModal';
import CsvRulesModal from './components/CsvRulesModal';
import ConfirmDeleteModal from './components/ConfirmDeleteModal';
import PageSizeSelect from './components/PageSizeSelect';
import DateInput from './components/DateInput';
import { downloadMarkersCsv } from './components/markerCsvExport';
import {
  text, unwrap, markerId, isEventMarker, isOpenNow, isPastEvent, isoDate, eventStart, createdAt, searchHaystack,
} from './components/markerUtils';
import './components/Marker-UI.css';
import './Dashboard-UI.css';
import PageTitle from '../../components/PageTitle';

// Every marker that is live in the app, one row each. Filters and sorting
// run on the client: the list is small enough, and it keeps the page
// instant. Editing opens MarkerDetailsModal, which saves through the
// backend's draft + approve flow.

const FETCH_PAGE_SIZE = 5000;
const DEFAULT_PAGE_SIZE = 20;

export const DEFAULT_QUERY = {
  search: '',
  type: 'all', // all | events | clubs | open
  from: '',
  to: '',
  showPast: false,
  sort: { by: 'date', dir: 'asc' },
};

const SORT_OPTIONS = [
  ['date:asc', 'Datum, nächste zuerst'],
  ['date:desc', 'Datum, späteste zuerst'],
  ['title:asc', 'Titel A–Z'],
  ['title:desc', 'Titel Z–A'],
  ['created:desc', 'Zuletzt angelegt'],
];

const byTitle = (a, b) => text(a.title).localeCompare(text(b.title), 'de', { sensitivity: 'base' });

function comparator({ by, dir }, now) {
  const sign = dir === 'desc' ? -1 : 1;
  if (by === 'title') return (a, b) => sign * byTitle(a, b);
  if (by === 'created') {
    return (a, b) => {
      const ta = createdAt(a)?.getTime() ?? 0;
      const tb = createdAt(b)?.getTime() ?? 0;
      return sign * (ta - tb) || byTitle(a, b);
    };
  }
  // Date: events in date order, then clubs (open ones first, then by name).
  return (a, b) => {
    const ea = isEventMarker(a);
    const eb = isEventMarker(b);
    if (ea !== eb) return ea ? -1 : 1;
    if (ea) {
      const ta = eventStart(a)?.getTime() ?? Number.POSITIVE_INFINITY;
      const tb = eventStart(b)?.getTime() ?? Number.POSITIVE_INFINITY;
      if (ta !== tb) return sign * (ta < tb ? -1 : 1);
      return byTitle(a, b);
    }
    const oa = isOpenNow(a, now) ? 0 : 1;
    const ob = isOpenNow(b, now) ? 0 : 1;
    return oa - ob || byTitle(a, b);
  };
}

/** Filters and sorts; also counts the past events the filter hid. */
export function applyQuery(markers, query, now = new Date()) {
  let rows = markers;
  if (query.type === 'events') rows = rows.filter(isEventMarker);
  else if (query.type === 'clubs') rows = rows.filter((m) => !isEventMarker(m));
  else if (query.type === 'open') rows = rows.filter((m) => isOpenNow(m, now));

  if (query.from || query.to) {
    rows = rows.filter((m) => {
      if (!isEventMarker(m)) return false;
      const iso = isoDate(m);
      if (!iso) return false;
      if (query.from && iso < query.from) return false;
      if (query.to && iso > query.to) return false;
      return true;
    });
  }

  const past = rows.filter((m) => isPastEvent(m, now)).length;
  if (!query.showPast) rows = rows.filter((m) => !isPastEvent(m, now));

  const q = query.search.trim().toLowerCase();
  if (q) rows = rows.filter((m) => searchHaystack(m).includes(q));

  return { rows: [...rows].sort(comparator(query.sort, now)), past };
}

function useNow(intervalMs) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), intervalMs);
    return () => clearInterval(timer);
  }, [intervalMs]);
  return now;
}

const Icon = ({ path, size = 14 }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
    strokeLinejoin="round" width={size} height={size} aria-hidden="true">{path}</svg>
);

const icons = {
  plus: <path d="M12 5v14M5 12h14" />,
  upload: <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></>,
  download: <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></>,
  rules: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></>,
  refresh: <><path d="M3 12a9 9 0 1 0 9-9" /><path d="M3 3v6h6" /></>,
  search: <><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></>,
  calendar: <><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></>,
  x: <path d="M18 6 6 18M6 6l12 12" />,
  trash: <><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></>,
};

export default function Dashboard() {
  const [markers, setMarkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState(DEFAULT_QUERY);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [selected, setSelected] = useState(() => new Set());
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const [busy, setBusy] = useState(false);
  const now = useNow(60_000);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await MarkersAPI.getAllMarkers({ adminAccepted: true, page: 0, size: FETCH_PAGE_SIZE, _t: Date.now() });
      const items = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
      setMarkers(items.filter((m) => unwrap(m.adminAccepted) === true));
    } catch (err) {
      console.error('Marker konnten nicht geladen werden:', err);
      setError('Marker konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), 4500);
    return () => clearTimeout(timer);
  }, [toast]);

  const notify = (message, kind = 'ok') => setToast({ message, kind });

  const { rows, past } = useMemo(() => applyQuery(markers, query, now), [markers, query, now]);

  const stats = useMemo(() => ({
    total: markers.length,
    events: markers.filter(isEventMarker).length,
    upcoming: markers.filter((m) => isEventMarker(m) && !isPastEvent(m, now)).length,
    clubs: markers.filter((m) => !isEventMarker(m)).length,
    open: markers.filter((m) => isOpenNow(m, now)).length,
  }), [markers, now]);

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const currentPage = Math.min(page, totalPages - 1);
  const pageRows = rows.slice(currentPage * pageSize, currentPage * pageSize + pageSize);
  const filtered = query.search || query.type !== 'all' || query.from || query.to || query.showPast;

  const patch = (changes) => { setQuery((q) => ({ ...q, ...changes })); setPage(0); };
  const resetQuery = () => { setQuery(DEFAULT_QUERY); setPage(0); };
  const toggleSort = (by) => setQuery((q) => ({
    ...q,
    sort: { by, dir: q.sort.by === by && q.sort.dir === 'asc' ? 'desc' : 'asc' },
  }));

  const replaceMarker = (updated) => setMarkers((prev) =>
    prev.map((m) => (String(markerId(m)) === String(markerId(updated)) ? updated : m)));

  const removeMarkers = (ids) => {
    const gone = new Set(ids.map(String));
    setMarkers((prev) => prev.filter((m) => !gone.has(String(markerId(m)))));
    setSelected((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.delete(id));
      return next;
    });
  };

  const toggleSelect = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const selectAll = (ids, on) => setSelected((prev) => {
    const next = new Set(prev);
    ids.forEach((id) => (on ? next.add(id) : next.delete(id)));
    return next;
  });

  const handleSaved = (updated) => {
    replaceMarker(updated);
    setEditing(null);
    notify(`„${text(updated.title)}“ gespeichert – die Änderungen sind in der App.`);
  };

  const handleUnapprove = async (marker) => {
    setBusy(true);
    try {
      await MarkersAPI.unapproveMarker(markerId(marker));
      removeMarkers([markerId(marker)]);
      setEditing(null);
      notify(`„${text(marker.title)}“ ist aus der App genommen und liegt unter Pending Events.`);
    } catch (err) {
      console.error('Unapprove fehlgeschlagen:', err);
      notify('Der Marker konnte nicht zurückgenommen werden.', 'error');
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    const list = confirm.markers;
    setConfirm(null);
    setBusy(true);
    const results = await Promise.allSettled(list.map((m) => MarkersAPI.deleteMarker(markerId(m))));
    const deleted = list.filter((_, i) => results[i].status === 'fulfilled');
    removeMarkers(deleted.map(markerId));
    if (editing && deleted.some((m) => String(markerId(m)) === String(markerId(editing)))) setEditing(null);
    const failed = list.length - deleted.length;
    if (failed) notify(`${deleted.length} gelöscht, ${failed} fehlgeschlagen.`, 'error');
    else if (deleted.length === 1) notify(`„${text(deleted[0].title)}“ gelöscht.`);
    else notify(`${deleted.length} Marker gelöscht.`);
    setBusy(false);
  };

  const selectedMarkers = markers.filter((m) => selected.has(markerId(m)));

  // Exports what the list shows, filters and sort included, in the CSV import's format.
  const exportCsv = () => {
    if (rows.length === 0) return;
    try {
      const filename = downloadMarkersCsv(rows);
      notify(`${rows.length} Marker als ${filename} exportiert – die Datei lässt sich über den CSV-Import wieder einspielen.`);
    } catch (err) {
      console.error('CSV-Export fehlgeschlagen:', err);
      notify('Der CSV-Export ist fehlgeschlagen.', 'error');
    }
  };

  const pageNumbers = () => {
    const pages = [];
    const left = Math.max(0, currentPage - 2);
    const right = Math.min(totalPages - 1, currentPage + 2);
    if (left > 0) { pages.push(0); if (left > 1) pages.push('…'); }
    for (let i = left; i <= right; i += 1) pages.push(i);
    if (right < totalPages - 1) { if (right < totalPages - 2) pages.push('…'); pages.push(totalPages - 1); }
    return pages;
  };

  const typeButtons = [
    ['all', 'Alle', stats.total],
    ['events', 'Events', stats.events],
    ['clubs', 'Clubs', stats.clubs],
    ['open', 'Jetzt offen', stats.open],
  ];

  return (
    <div className="page db-page">
      <div className="page-header">
        <PageTitle title="Events & Clubs" description="Alle Events und Clubs, die in der App zu sehen sind. Hier bearbeitest, löschst oder legst du sie an – einzeln oder per CSV." />
        <div className="db-header-actions">
          <Link className="btn db-btn" to="/create-club"><Icon path={icons.plus} /> Club</Link>
          <Link className="btn db-btn" to="/manage-events"><Icon path={icons.plus} /> Event</Link>
          <button type="button" className="btn db-btn" onClick={() => setBulkOpen(true)}><Icon path={icons.upload} /> CSV-Import</button>
          <button type="button" className="btn db-btn" onClick={exportCsv} disabled={loading || rows.length === 0}
            title="Lädt die aktuell gefilterte Liste als CSV herunter – im Format des CSV-Imports, also wieder einspielbar">
            <Icon path={icons.download} /> CSV-Export{!loading && rows.length > 0 && <span className="db-count">{rows.length}</span>}
          </button>
          <button type="button" className="btn db-btn" onClick={() => setRulesOpen(true)}
            title="Alle Spalten, Pflichtfelder und erlaubten Werte des CSV-Imports als Text zum Kopieren, z. B. für einen LLM-Prompt">
            <Icon path={icons.rules} /> CSV-Regeln
          </button>
          <button type="button" className="btn btn-ghost db-btn" onClick={load} disabled={loading} aria-label="Aktualisieren" title="Aktualisieren">
            <Icon path={icons.refresh} /> Aktualisieren
          </button>
        </div>
      </div>

      <div className="db-stats">
        <button type="button" className={`db-stat${query.type === 'all' ? ' db-stat--active' : ''}`} onClick={() => patch({ type: 'all' })}>
          <span className="db-stat-label">Live in der App</span>
          <span className="db-stat-value">{loading ? '–' : stats.total}</span>
          <span className="db-stat-sub">Events und Clubs</span>
        </button>
        <button type="button" className={`db-stat db-stat--events${query.type === 'events' ? ' db-stat--active' : ''}`} onClick={() => patch({ type: 'events' })}>
          <span className="db-stat-label">Events</span>
          <span className="db-stat-value">{loading ? '–' : stats.events}</span>
          <span className="db-stat-sub">{loading ? '' : `${stats.upcoming} anstehend`}</span>
        </button>
        <button type="button" className={`db-stat db-stat--clubs${query.type === 'clubs' ? ' db-stat--active' : ''}`} onClick={() => patch({ type: 'clubs' })}>
          <span className="db-stat-label">Clubs</span>
          <span className="db-stat-value">{loading ? '–' : stats.clubs}</span>
          <span className="db-stat-sub">mit Öffnungszeiten</span>
        </button>
        <button type="button" className={`db-stat db-stat--open${query.type === 'open' ? ' db-stat--active' : ''}`} onClick={() => patch({ type: 'open' })}>
          <span className="db-stat-label">Jetzt geöffnet</span>
          <span className="db-stat-value">{loading ? '–' : stats.open}</span>
          <span className="db-stat-sub">Clubs zur aktuellen Uhrzeit</span>
        </button>
      </div>

      <div className="db-toolbar">
        <label className="db-search">
          <Icon path={icons.search} />
          <input type="search" value={query.search} onChange={(e) => patch({ search: e.target.value })}
            placeholder="Titel, Adresse, Genre, Act, @account, ID…" aria-label="Marker durchsuchen" />
        </label>

        <div className="db-seg" role="group" aria-label="Typ">
          {typeButtons.map(([value, label, count]) => (
            <button key={value} type="button" className={`db-seg-btn${query.type === value ? ' db-seg-btn--active' : ''}`}
              aria-pressed={query.type === value} onClick={() => patch({ type: value })}>
              {label}{!loading && <span className="db-count">{count}</span>}
            </button>
          ))}
        </div>

        {query.type !== 'clubs' && query.type !== 'open' && (
          <>
            <div className="db-dates" title="Events in diesem Zeitraum">
              <Icon path={icons.calendar} size={13} />
              <DateInput value={query.from} onChange={(v) => patch({ from: v })} maxDate={query.to || undefined} ariaLabel="Datum von" />
              <span className="db-dash">–</span>
              <DateInput value={query.to} onChange={(v) => patch({ to: v })} minDate={query.from || undefined} ariaLabel="Datum bis" />
            </div>
            <label className="db-toggle">
              <input type="checkbox" checked={query.showPast} onChange={(e) => patch({ showPast: e.target.checked })} />
              Vergangene Events{past > 0 && !query.showPast && <span className="db-count">{past}</span>}
            </label>
          </>
        )}

        <select className="db-sort" aria-label="Sortierung" value={`${query.sort.by}:${query.sort.dir}`}
          onChange={(e) => { const [by, dir] = e.target.value.split(':'); patch({ sort: { by, dir } }); }}>
          {SORT_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>

        {filtered && (
          <button type="button" className="db-reset" onClick={resetQuery}><Icon path={icons.x} size={12} /> Zurücksetzen</button>
        )}

        <span className="db-spacer" />
        {!loading && <span className="db-result">{rows.length} {rows.length === 1 ? 'Treffer' : 'Treffer'}</span>}
      </div>

      {selected.size > 0 && (
        <div className="db-bulkbar" role="region" aria-label="Auswahl">
          <strong>{selected.size} ausgewählt</strong>
          <button type="button" className="btn btn-danger" onClick={() => setConfirm({ markers: selectedMarkers })} disabled={busy}>
            <Icon path={icons.trash} size={13} /> Auswahl löschen
          </button>
          <button type="button" className="btn btn-ghost" onClick={() => setSelected(new Set())}>Auswahl aufheben</button>
        </div>
      )}

      {error && (
        <div className="error-state" role="alert">
          <span>{error}</span>
          <button type="button" className="btn btn-ghost" style={{ marginLeft: 'auto' }} onClick={load}>Erneut laden</button>
        </div>
      )}

      {loading ? (
        <div className="db-skeleton" aria-busy="true" aria-label="Lädt Marker">
          {Array.from({ length: 6 }).map((_, i) => <div key={i} className="db-skeleton-row" />)}
        </div>
      ) : rows.length === 0 ? (
        <div className="db-empty">
          <strong>{markers.length === 0 ? 'Noch keine Marker in der App' : 'Keine Treffer'}</strong>
          <span>
            {markers.length === 0
              ? 'Neue Events und Clubs erscheinen hier, sobald sie freigegeben sind.'
              : past > 0 && !query.showPast
                ? `${past} vergangene Events sind ausgeblendet.`
                : 'Andere Suche oder Filter probieren.'}
          </span>
          {filtered && <button type="button" className="btn" onClick={resetQuery}>Filter zurücksetzen</button>}
        </div>
      ) : (
        <MarkerTable
          rows={pageRows}
          selectedIds={selected}
          onToggleSelect={toggleSelect}
          onSelectAll={selectAll}
          onOpen={setEditing}
          onDelete={(marker) => setConfirm({ markers: [marker] })}
          sort={query.sort}
          onSort={toggleSort}
          now={now}
        />
      )}

      {!loading && rows.length > 0 && (
        <div className="db-pager">
          <span>
            {currentPage * pageSize + 1}–{Math.min(rows.length, (currentPage + 1) * pageSize)} von {rows.length}
          </span>
          <div className="db-pager-controls">
            <button type="button" className="db-pager-btn" onClick={() => setPage(Math.max(0, currentPage - 1))} disabled={currentPage === 0} aria-label="Vorherige Seite">‹</button>
            {pageNumbers().map((p, i) => (p === '…'
              ? <span key={`e${i}`} className="db-pager-ellipsis">…</span>
              : <button key={p} type="button" className={`db-pager-btn${p === currentPage ? ' db-pager-btn--active' : ''}`}
                  onClick={() => setPage(p)} aria-current={p === currentPage ? 'page' : undefined} aria-label={`Seite ${p + 1}`}>{p + 1}</button>))}
            <button type="button" className="db-pager-btn" onClick={() => setPage(Math.min(totalPages - 1, currentPage + 1))} disabled={currentPage >= totalPages - 1} aria-label="Nächste Seite">›</button>
            <PageSizeSelect value={pageSize} onChange={(v) => { setPage(0); setPageSize(v); }} />
          </div>
        </div>
      )}

      {editing && (
        <MarkerDetailsModal
          marker={editing}
          onClose={() => setEditing(null)}
          onSaved={handleSaved}
          onDelete={(marker) => setConfirm({ markers: [marker] })}
          onUnapprove={handleUnapprove}
          busy={busy}
        />
      )}

      <BulkCsvUploadModal open={bulkOpen} onClose={() => setBulkOpen(false)} onUploaded={() => load()} />
      <CsvRulesModal open={rulesOpen} onClose={() => setRulesOpen(false)} />

      <ConfirmDeleteModal
        open={Boolean(confirm)}
        markerTitle={confirm?.markers.length === 1 ? text(confirm.markers[0].title) : undefined}
        title={confirm && confirm.markers.length > 1 ? `${confirm.markers.length} Marker löschen?` : undefined}
        message={confirm && confirm.markers.length > 1 ? `${confirm.markers.length} Marker werden dauerhaft aus der App entfernt.` : undefined}
        confirmLabel={confirm && confirm.markers.length > 1 ? `${confirm.markers.length} Marker löschen` : undefined}
        onConfirm={confirmDelete}
        onCancel={() => setConfirm(null)}
      />

      {toast && <div className={`db-toast${toast.kind === 'error' ? ' db-toast--error' : ''}`} role="status">{toast.message}</div>}
    </div>
  );
}
